export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  role: UserRole
  avatar?: string
  createdAt: string
  updatedAt: string
}

export enum UserRole {
  ADMIN = 'admin',
  USER = 'user',
  MODERATOR = 'moderator',
}

export interface ApiResponse<T> {
  data: T
  message?: string
  success: boolean
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  accessToken: string | null
}

export interface RouteConfig {
  path: string
  element: React.ComponentType
  protected?: boolean
  roles?: UserRole[]
}

// Data Categories Types
export interface DataCategory {
  id: string
  code: string
  name: string
  description?: string
  type: string
  metadata?: Record<string, any>
  createdAt: string
  updatedAt: string
}

// Source Types
export interface SourceType {
  id: number
  code: string
  display_name: string
  category: string
  description: string
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
  created_by: string
  updated_by: string
  deleted_at: string | null
  deleted_by: string | null
}

export interface SourceTypesResponse {
  success: boolean
  message: string
  data: SourceType[]
  total_count: number
  page: number
  per_page: number
  has_more: boolean
}

export interface SourceTypeResponse {
  success: boolean
  message: string
  data: SourceType
}

// Data Source Enums
export enum UpdateFrequency {
  DAILY = 'Daily',
  WEEKLY = 'Weekly',
  HOURLY = 'Hourly',
  REAL_TIME = 'Real-time'
}

export enum ConnectionStatus {
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected'
}

export enum IngestionMethod {
  FILE_UPLOAD = 'File Upload',
  S3_CSV = 'CSV File Import',
}

// Processing Jobs Types
export interface ProcessingJob {
  id: number
  job_id: string
  upload_id: number
  data_source_id: number
  data_source_name: string
  file_name: string
  processing_method: string
  total_records: number | null
  status: 'Failed' | 'Pending' | 'Processing' | 'Completed' | 'Completed with errors' | 'Processed' | 'Not started'
  records_processed: number | null
  records_failed: number | null
  started_at: string | null
  completed_at: string | null
  created_at: string
  file_size: string | null
}

export interface ProcessingJobsResponse {
  success: boolean
  message: string
  data: ProcessingJob[]
  has_more: boolean
  page: number
  per_page: number
  total_count: number
}

// Dashboard Aggregates Types
export interface DashboardAggregates {
  total_records_processed: number
  total_conflicts_resolved: number
  active_validation_rules: number
  active_identity_rules: number
  total_records_failed: number
  time_period: string
  last_updated: string
}

export interface DashboardAggregatesResponse {
  success: boolean
  message: string
  data: DashboardAggregates
}

// Recent Activity Types
export interface RecentActivity {
  time_window: string
  time_window_start: string
  time_window_end: string
  manual_uploads: number
  total_uploads: number
  average_processing_time_seconds: number
  median_processing_time_seconds: number
}

export interface RecentActivityResponse {
  success: boolean
  message: string
  data: RecentActivity
}

// Conflicts Types
export interface ConflictField {
  field?: string
  field_label?: string
  source_value?: string
  source_date?: string | null
  new_value?: string
  resolved_value?: string
  resolution_strategy?: string
  reasoning?: string
}

export interface ConflictRecord {
  record_id?: string
  matched_profile_id?: number
  timestamp?: string
  conflicts?: ConflictField[]
  total_conflicts?: number
}

export interface ConflictsPagination {
  page: number
  per_page: number
  total_count: number
  has_more: boolean
}

export interface ConflictsResponse {
  success: boolean
  message: string
  data: ConflictRecord[]
  pagination: ConflictsPagination
}

// Validation Rules Types
export interface ValidationRule {
  rule_name: string
  canonical_field: string
  description: string
  applied_count: number
  success_count: number
  failed_count: number
  success_rate: number
  status: string
}

export interface ValidationRulesResponse {
  success: boolean
  message: string
  data: ValidationRule[]
  total_rules_applied: number
}

// Identity Resolution Types
export interface DecisionBreakdown {
  AUTO_MERGE?: number
  MERGE_WITH_REVIEW?: number
  NEW_ENTITY?: number
}

export interface IdentityResolutionRule {
  rule_name?: string
  match_type?: string
  description?: string
  matches_found?: number
  average_confidence?: number
  confidence_level?: 'High' | 'Medium' | 'Low'
  decision_breakdown?: DecisionBreakdown
}

export interface IdentityResolutionResponse {
  success: boolean
  message: string
  data: IdentityResolutionRule[]
  total_identities_resolved: number
}

// Manual Review Error Types
export interface ManualReviewError {
  id: number
  job_id: string
  record_id: string
  review_type: string
  error_category: string
  error_message: string
  record_data: Record<string, any>
  reviewed: boolean
  reviewed_by: string | null
  reviewed_at: string | null
  resolution_notes: string | null
  created_at: string
  updated_at: string
}

export interface ManualReviewErrorsParams {
  pending_only?: boolean
  error_category?: string
}

export interface ManualReviewErrorsResponse {
  success: boolean
  message: string
  data: ManualReviewError[]
  total_count: number
  pending_count: number
  reviewed_count: number
  job_id: string
}

// Governed Clients Types
export interface GovernedClient {
  id: number
  client_identifier: string
  first_name: string
  last_name: string
  designation: string
  company: string
  date_of_birth: string
  joining_date: string
  is_active: boolean
  risk_profile: string
  contact_info: any
  accounts: any[]
  created_at: string
  updated_at: string
}

export interface GovernedClientsPagination {
  page: number
  page_size: number
  total_records: number
  total_pages: number
}

export interface GovernanceMetadata {
  temporal_filter_applied: string
  fields_masked: string[]
  query_complexity_score: number
  quota_remaining: number | null
}

export interface DataQualityMetadata {
  completeness_pct: number
  freshness_timestamp: string
  validation_pass_rate: number
  trust_score: number
  trust_level: string
}

export interface RequestMetadata {
  correlation_id: string
  timestamp: string
}

export interface GovernedClientsResponse {
  data: GovernedClient[]
  pagination: GovernedClientsPagination
  governance: GovernanceMetadata
  data_quality: DataQualityMetadata
  metadata: RequestMetadata
}

// Client Portfolio Types
export interface ClientContactInfo {
  email_address?: string
  phone_number?: string
  social_security_number?: string
  address_line_1?: string
  address_line_2?: string
  city?: string
  state?: string
  country?: string
  mailing_zip_code?: string
  emergency_contact_name?: string
}

export interface ClientAccount {
  id: number
  account_identifier: string
  client_identifier: string
  client_name: string
  account_type: string
  account_name: string
  status: string
  amount: number
  created_at: string
  updated_at: string
}

export interface ClientPortfolioData {
  id: number
  client_identifier: string
  first_name: string
  last_name: string
  designation: string
  company: string
  date_of_birth: string
  joining_date: string
  is_active: boolean
  risk_profile: string
  source_list: string[]
  contact_info: ClientContactInfo
  accounts: ClientAccount[]
  created_at: string
  updated_at: string
}

export interface AssetAllocation {
  equity_percentage: number
  fixed_income_percentage: number
  cash_percentage: number
  alternative_percentage: number
}

export interface PortfolioSummary {
  total_market_value: number
  total_cost_basis: number
  total_unrealized_gain_loss: number
  unrealized_gain_loss_pct: number
  cash_balance: number
  holdings_count: number
  asset_allocation: AssetAllocation
  last_calculated_at: string
}

export interface PortfolioHolding {
  symbol: string
  security_name: string
  cusip: string
  asset_class: string
  asset_sub_class: string
  sector: string
  quantity: number
  unit_cost: number
  current_price: number
  market_value: number
  total_cost: number
  unrealized_gain_loss: number
  unrealized_gain_loss_pct: number
  position_type: string
  acquisition_date: string
  last_price_update: string
}

export interface PortfolioAccount {
  client_identifier: string
  client_name: string
  client_risk_profile: string
  account_identifier: string
  account_type: string
  account_name: string
  account_status: string
  summary: PortfolioSummary
  holdings: PortfolioHolding[]
}

export interface ClientPortfolioResponse {
  data: {
    client: ClientPortfolioData
    portfolio: PortfolioAccount[]
    has_client_data: boolean
    has_portfolio_data: boolean
    total_portfolio_accounts: number
    total_portfolio_value: number
  }
  governance: GovernanceMetadata
  data_quality: DataQualityMetadata
  metadata: RequestMetadata
}